import { LuArrowUpDown, LuChevronDown, LuChevronUp } from "react-icons/lu";

const COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[7%]" },
  { key: "created", name: "Krijuar", sort_value: "created_at", className: "w-[18%]" },
  { key: "action", name: "Veprimi", sort_value: "action_type", className: "w-[18%]" },
  { key: "table", name: "Tabela", sort_value: "table_name", className: "w-[20%]" },
  { key: "user", name: "Përdoruesi", sort_value: "first_name", className: "w-[22%]" },
  { key: "role", name: "Roli", sort_value: "user_role", className: "w-[15%]" },
];

const ACTION_PILL = {
  create: "bg-gray-100 text-gray-700 ring-gray-200",
  update: "bg-blue-50 text-blue-700 ring-blue-200",
  submit: "bg-gray-100 text-gray-700 ring-gray-200",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  approve: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rejected: "bg-red-50 text-red-700 ring-red-200",
  reject: "bg-red-50 text-red-700 ring-red-200",
  "soft delete": "bg-red-50 text-red-700 ring-red-200",
  delete: "bg-red-50 text-red-700 ring-red-200",
  bulk_create: "bg-amber-50 text-amber-700 ring-amber-200",
};

const TABLE_LABELS = {
  pending_receipts: "Pending Receipts",
  submitted_receipts: "Submitted Receipts",
  receipts: "Receipts",
  users: "Users",
  notifications: "Notifications",
  complaints: "Complaints",
  games: "Games",
  game_rules: "Game Rules",
  winners: "Winners",
  business_winners: "Business Winners",
  businesses: "Businesses",
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");

const formatAction = (a) => {
  if (!a) return "—";
  return a
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((w) => cap(w))
    .join(" ");
};

const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });
  } catch {
    return d;
  }
};

const ActionPill = ({ action }) => {
  const key = action?.toLowerCase().replace(/_/g, " ");
  const cls = ACTION_PILL[key] || ACTION_PILL[action?.toLowerCase()] || "bg-gray-100 text-gray-700 ring-gray-200";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset ${cls}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      {formatAction(action)}
    </span>
  );
};

const TablePill = ({ name }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200">
    {TABLE_LABELS[name] || name || "—"}
  </span>
);

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-28 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-5 w-24 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-32 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-16 bg-gray-200 rounded" /></td>
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active)
    return (
      <LuArrowUpDown
        size={12}
        className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    );
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

export default function AnalyticsTable({
  logs,
  isLoading,
  params = {},
  onSort,
  onRowClick,
}) {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (!logs || logs.length === 0)
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : logs?.map((item, idx) => (
                  <tr
                    key={item.id ?? idx}
                    onClick={() => onRowClick?.(item)}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                  >
                    <td className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">
                      #{item.id}
                    </td>
                    <td className="px-6 py-3.5 text-gray-700 whitespace-nowrap tabular-nums text-xs">
                      {formatDateTime(item.created_at)}
                    </td>
                    <td className="px-6 py-3.5">
                      <ActionPill action={item.action_type} />
                    </td>
                    <td className="px-6 py-3.5">
                      <TablePill name={item.table_name} />
                    </td>
                    <td className="px-6 py-3.5 text-gray-700 truncate">
                      {(item.first_name || item.last_name)
                        ? `${item.first_name || ""} ${item.last_name || ""}`.trim()
                        : "—"}
                    </td>
                    <td className="px-6 py-3.5 text-gray-600 text-xs uppercase tracking-wide">
                      {item.user_role || "—"}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading && (!logs || logs.length === 0) ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          logs?.map((item, idx) => (
            <button
              key={item.id ?? idx}
              type="button"
              onClick={() => onRowClick?.(item)}
              className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <ActionPill action={item.action_type} />
                <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                  {formatDateTime(item.created_at)}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <TablePill name={item.table_name} />
                <span className="text-xs text-gray-500 tabular-nums">#{item.id}</span>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-xs text-gray-600">
                <span className="truncate">
                  {(item.first_name || item.last_name)
                    ? `${item.first_name || ""} ${item.last_name || ""}`.trim()
                    : "—"}
                </span>
                <span className="uppercase tracking-wide text-gray-500">{item.user_role}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
